import {useState} from "react";
import {useForm, Controller} from "react-hook-form";
import {App as AntdApp, Table, Tag, Popconfirm, Segmented, Button as AntButton} from "antd";
import dayjs from "dayjs";
import {PageHeader, ModalForm} from "~/components";
import {TextAreaField} from "~/components/Forms";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {useGetExtraPurchasesQuery, useResolveExtraPurchaseMutation} from "~/reduxs/api/memberApiSlice";
import {nf} from "../util";

const STATUS_TAG = {
	pending: {color: 'gold', label: 'Chờ duyệt'},
	approved: {color: 'green', label: 'Đã duyệt'},
	rejected: {color: 'red', label: 'Từ chối'},
};

/** Modal nhập lý do khi từ chối đơn mua (tuỳ chọn — user sẽ thấy trong thông báo). */
function RejectModal({open, submitting, onCancel, onSubmit}) {
	const {control, handleSubmit, reset, formState: {errors}} = useForm({defaultValues: {note: ''}});

	return (
		<ModalForm
			open={open}
			icon="fa-light fa-circle-xmark"
			title="Từ chối đơn mua Gói Extra"
			subtitle="Lý do sẽ được gửi tới thành viên qua thông báo"
			onCancel={() => { reset({note: ''}); onCancel(); }}
			onOk={handleSubmit((v) => onSubmit(v, () => reset({note: ''})))}
			okText="Từ chối"
			loading={submitting}
		>
			<Controller control={control} name="note" render={({field}) => (
				<TextAreaField label="Lý do (tuỳ chọn)" placeholder="VD: chưa xác nhận được thanh toán..." rows={3} errors={errors} {...field} />
			)} />
		</ModalForm>
	);
}

/**
 * Trang "Đơn mua Gói Extra" (khu quản trị) — user gửi từ /subscription; admin duyệt (cộng
 * hạn mức vào CHU KỲ GÓI HIỆN TẠI ngay lúc duyệt) hoặc từ chối kèm lý do. Tách từ modal cũ
 * ở màn Quản lý thành viên.
 */
function ExtraPurchases() {
	const {notification} = AntdApp.useApp();
	const [status, setStatus] = useState('pending');
	const {data, isLoading} = useGetExtraPurchasesQuery(status);
	const [resolvePurchase, {isLoading: resolving}] = useResolveExtraPurchaseMutation();
	const [rejectRow, setRejectRow] = useState(null);

	const items = data?.items || [];

	const doResolve = async (row, action, note = '') => {
		try {
			const res = await resolvePurchase({id: row.id, action, note}).unwrap();
			notification.success({message: res?.message || 'Đã xử lý đơn mua'});
			setRejectRow(null);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không xử lý được đơn mua.')});
		}
	};

	const columns = [
		{
			title: 'Thành viên',
			key: 'member',
			render: (_, r) => (
				<div>
					<div style={{fontWeight: 600}}>{r.name || `@${r.username}`}</div>
					<div style={{fontSize: 12, color: '#8c8c8c'}}>@{r.username}{r.email ? ` · ${r.email}` : ''}</div>
				</div>
			),
		},
		{
			title: 'Gói Extra',
			key: 'pack',
			render: (_, r) => (
				<div>
					<div style={{fontWeight: 600}}>{r.pack_name}</div>
					<div style={{fontSize: 12, color: '#8c8c8c'}}>
						+{nf(r.units)} bài · {r.price > 0 ? `${nf(r.price)}đ` : 'Tặng'}
					</div>
				</div>
			),
		},
		// Cột phụ — ẩn trên mobile
		{title: 'Lời nhắn', dataIndex: 'note', key: 'note', responsive: ['md'], render: (v) => v || '—'},
		{
			title: 'Thời gian', dataIndex: 'created', key: 'created', width: 150, responsive: ['md'],
			render: (v) => (v ? dayjs(v).format('HH:mm DD/MM/YYYY') : '—'),
		},
		...(status !== 'pending' ? [{
			title: 'Trạng thái', key: 'status', width: 130,
			render: (_, r) => {
				const m = STATUS_TAG[r.status] || {color: 'default', label: r.status};
				return (
					<span>
						<Tag color={m.color}>{m.label}</Tag>
						{r.status === 'approved' && r.granted_period ? (
							<span style={{fontSize: 12, color: '#8c8c8c'}}>{r.granted_period}</span>
						) : null}
					</span>
				);
			},
		}] : []),
		...(status === 'pending' ? [{
			title: '', key: 'action', align: 'right', width: 190,
			render: (_, r) => (
				<span style={{display: 'inline-flex', gap: 8}}>
					<Popconfirm
						title={`Cộng ${nf(r.units)} bài vào hạn mức CHU KỲ GÓI HIỆN TẠI của @${r.username}?`}
						okText="Duyệt"
						cancelText="Huỷ"
						onConfirm={() => doResolve(r, 'approve')}
					>
						<AntButton type="primary" size="small" loading={resolving}>Duyệt</AntButton>
					</Popconfirm>
					<AntButton danger size="small" onClick={() => setRejectRow(r)}>Từ chối</AntButton>
				</span>
			),
		}] : []),
	];

	return (
		<div className="container">
			<PageHeader
				icon="fa-light fa-cart-plus"
				title="Đơn mua Gói Extra"
				subtitle="Duyệt hoặc từ chối đơn mua thêm hạn mức (Gói Extra) do thành viên gửi."
			/>

			<div style={{marginBottom: 12}}>
				<Segmented
					value={status}
					onChange={setStatus}
					options={[
						{value: 'pending', label: 'Chờ duyệt'},
						{value: 'approved', label: 'Đã duyệt'},
						{value: 'rejected', label: 'Từ chối'},
					]}
				/>
			</div>

			<Table
				columns={columns}
				dataSource={items}
				rowKey="id"
				loading={isLoading}
				size="small"
				scroll={{x: 'max-content'}}
				pagination={{pageSize: 20, hideOnSinglePage: true}}
				locale={{emptyText: status === 'pending' ? 'Không có đơn mua nào đang chờ duyệt.' : 'Chưa có đơn mua nào.'}}
			/>

			<RejectModal
				open={Boolean(rejectRow)}
				submitting={resolving}
				onCancel={() => setRejectRow(null)}
				onSubmit={({note}, resetForm) => doResolve(rejectRow, 'reject', note).then(resetForm)}
			/>
		</div>
	);
}

export default ExtraPurchases;
